import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { CASES_KEY, createCaseFromMail, MailAlreadyFiledError, ProblemError } from "@/api";
import { isDrawing } from "@/components/DropArea";
import { markArrival } from "@/lib/eingangArrival";
import { isMail } from "@/lib/mail";

/** The Vorgang a mail already lies in — what the screen offers instead of a second one. */
export interface ExistingMailCase {
  caseId: string;
  caseNo: string;
}

export interface MailDrop {
  /**
   * A drop that holds a mail — from the page, the drop area or the picker.
   * Resolves when the call is over; on success the screen has moved on to the
   * Eingang by then.
   */
  receive(files: File[]): Promise<void>;
  /** The filing is under way. */
  busy: boolean;
  /** The sentence for a refusal, until the next drop. `null` while there is none. */
  error: string | null;
  /**
   * `error` is this page's own refusal, spoken before a byte was sent — not
   * the server's answer to a call. The screen leads with „Nichts wurde
   * übertragen." then, as the drawing path does.
   */
  refused: boolean;
  /** Set instead of `error` when this very mail lies in one of my own Vorgänge. */
  existing: ExistingMailCase | null;
}

const ONE_MAIL = "Leg genau eine Mail je Vorgang ab.";

/**
 * Names the files that will not be sent, the way the drawing path does
 * (`DropArea`'s refusal): a mail drop takes mails and drawings, nothing else,
 * and the whole drop is refused rather than the readable half of it.
 */
function neitherOf(files: File[]): string {
  const names = files.map((file) => file.name).join(", ");
  const rest = "leg eine Mail (.eml oder .msg) ab, dazu dürfen Zeichnungen (PDF, JPEG, PNG) kommen.";
  return files.length === 1
    ? `${names} ist weder Mail noch Zeichnung — ${rest}`
    : `${names} sind weder Mails noch Zeichnungen — ${rest}`;
}

function messageOf(cause: unknown): string {
  return cause instanceof ProblemError
    ? cause.userMessage
    : cause instanceof Error
      ? cause.message
      : String(cause);
}

/**
 * The mail's way in (spec 2026-10-20 §3.1): a customer's mail — with the
 * drawings that came with it — is let go anywhere on the start page, and one
 * call files it as a new Vorgang. The screen then opens the Eingang on that
 * mail (`?mail=<entryId>`), where a person chooses which drawing is read.
 *
 * **Nothing is transmitted to the provider here.** The mail is only stored;
 * what leaves the house is what a person picks as a drawing in the Eingang
 * (AN-08) — which is why this path has no reading to start.
 *
 * Exactly one mail per drop, checked before a byte is sent. The call itself is
 * one transaction on the server, so a failure leaves nothing behind and the
 * next drop simply sends everything again — there is no half-created Vorgang
 * to reuse, unlike the drawing path's retry.
 */
export function useMailDrop(): MailDrop {
  const navigate = useNavigate();
  const client = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refused, setRefused] = useState(false);
  const [existing, setExisting] = useState<ExistingMailCase | null>(null);
  // A second drop while the first is still on its way would file the same mail
  // twice. State is too late for that (the next render), a ref is not.
  const filing = useRef(false);

  const receive = useCallback(
    async (files: File[]): Promise<void> => {
      if (filing.current) return;
      setError(null);
      setRefused(false);
      setExisting(null);

      // Both refusals are spoken here, before a byte is sent.
      const neither = files.filter((file) => !isMail(file) && !isDrawing(file));
      if (neither.length > 0) {
        setError(neitherOf(neither));
        setRefused(true);
        return;
      }
      if (files.filter(isMail).length !== 1) {
        setError(ONE_MAIL);
        setRefused(true);
        return;
      }

      filing.current = true;
      setBusy(true);
      try {
        const filed = await createCaseFromMail(files);
        // The new Vorgang belongs in „Zuletzt bearbeitet" the moment the
        // person comes back from the Eingang, not after the cache runs out —
        // the register's overview sits under the same key prefix.
        void client.invalidateQueries({ queryKey: CASES_KEY });
        // The Eingang ticks the drawings dropped with the mail on this first
        // showing only (M-7).
        markArrival(filed.case.id, filed.entryId);
        navigate(`/vorgang/${filed.case.id}/eingang?mail=${filed.entryId}`);
      } catch (cause) {
        if (cause instanceof MailAlreadyFiledError) {
          setExisting({ caseId: cause.caseId, caseNo: cause.caseNo });
        } else {
          setError(messageOf(cause));
        }
      } finally {
        filing.current = false;
        setBusy(false);
      }
    },
    [client, navigate],
  );

  return { receive, busy, error, refused, existing };
}
